/**
 * CA Intelligence — Personalized chat interface for CA firms.
 *
 * Like Glean/NotebookLM but for Indian CA practices:
 * - Answers questions using indexed data across ALL CA sources
 *   (Tally, GST Portal, IT Portal, TRACES, MCA21, Bank Statements)
 * - Cites sources with document references
 * - Proactively surfaces compliance risks, deadlines, ITC gaps
 * - Drafts responses, notices, letters on demand
 * - Competes with Google's Vertex AI + Workspace connector play
 *
 * Architecture:
 * - Uses existing POST /chat/conversations/{id}/messages/stream SSE endpoint
 * - CA-specific system prompt injected via conversation metadata
 * - Suggested prompts tuned for CA workflows
 * - Source citations linked back to original connector documents
 */

import { useState, useRef, useEffect, useCallback } from "react";

// ── CA-specific suggested prompts by category ─────────────────
const PROMPT_CATEGORIES = [
  {
    id: "compliance",
    label: "📅 Compliance",
    color: "#F59E0B",
    bg: "#FFFBEB",
    prompts: [
      "Which clients have overdue GST filings this month?",
      "Show me all notices due for response in the next 7 days",
      "What is the GSTR-3B filing status for Sharma Trading Co. for October 2024?",
      "List all TDS defaults across my client portfolio",
      "Which clients haven't filed their annual return (GSTR-9) for FY 2024-25?",
    ],
  },
  {
    id: "reconciliation",
    label: "🔄 Reconciliation",
    color: "#1D4ED8",
    bg: "#EFF6FF",
    prompts: [
      "What is the ITC mismatch for Mehta Exports LLP in October 2024?",
      "Show me all suppliers who haven't uploaded GSTR-1 for Q2",
      "Reconcile Tally purchases vs GSTR-2B for Kapoor Pharma Pvt Ltd",
      "Which invoices in Tally don't have a matching entry in GSTR-2B?",
      "What is the total ITC at risk across all my clients this quarter?",
    ],
  },
  {
    id: "client",
    label: "👤 Client Intel",
    color: "#6D28D9",
    bg: "#F5F3FF",
    prompts: [
      "Give me a complete compliance summary for Patel Construction",
      "What is the outstanding tax demand for Desai Agro Industries?",
      "Show me all notices and their status for Kapoor Pharma",
      "What documents are pending from Sharma Trading Co. this quarter?",
      "Summarize the financial health of Mehta Exports LLP from all connected sources",
    ],
  },
  {
    id: "tax",
    label: "💰 Tax & ITC",
    color: "#047857",
    bg: "#F0FDF4",
    prompts: [
      "Calculate advance tax for Sharma Trading Co. for Q3 FY 2024-25",
      "What is the estimated tax liability for all my company clients for AY 2025-26?",
      "Which clients are eligible for additional ITC in GSTR-9 not claimed in GSTR-3B?",
      "Show me all outstanding demands and interest computations across clients",
      "What is the section 234B interest exposure for clients who underpaid advance tax?",
    ],
  },
  {
    id: "draft",
    label: "✍️ Draft & Write",
    color: "#B91C1C",
    bg: "#FEF2F2",
    prompts: [
      "Draft a reply to the notice u/s 143(1) for Kapoor Pharma citing TDS timing difference",
      "Write a reminder email to Ganesh Textiles to upload their GSTR-1",
      "Draft a client communication about the new GST amnesty scheme",
      "Prepare a brief for Patel Construction on responding to the 142(1) notice",
      "Write a WhatsApp message to all clients with overdue GSTR-3B filings",
    ],
  },
];

// ── Mock response engine for demo ─────────────────────────────
const CA_MOCK_RESPONSES = {
  "overdue": {
    text: `Based on your connected data across **GST Portal**, **IT Portal**, and **TRACES**, here are the overdue filings as of today:

**🔴 Critical (>7 days overdue)**
1. **Kapoor Pharma Pvt Ltd** — ITR FY 2024-25 (8 days overdue) · Penalty u/s 234F: ₹5,000
2. **Desai Agro Industries** — GSTR-3B September (19 days overdue) · Late fee accrued: ₹950
3. **Mehta Exports LLP** — TDS Return Q2 (8 days overdue) · Interest u/s 234E: ₹1,600

**🟡 Due within 7 days**
4. **Mehta Exports LLP** — GSTR-1 October (due Nov 11, 3 days left)
5. **Patel Construction** — Notice 142(1) response (due Nov 15, 7 days left)

**Total estimated penalties if not filed today: ₹8,750**

Would you like me to draft WhatsApp reminders for all three critical clients?`,
    sources: [
      { title: "GSTR-3B Filing Status — All GSTINs", type: "gst_filing_status" },
      { title: "IT Portal ITR Status — Kapoor Pharma", type: "it_itr_status" },
      { title: "TRACES TDS Return Status — Q2", type: "tds_return_status" },
    ],
  },
  "itc mismatch": {
    text: `**ITC Reconciliation — Mehta Exports LLP (GSTIN: 27BBBFM8272G1ZP) — October 2024**

**Summary**
- Tally purchases: ₹4,28,000 across 12 invoices
- GSTR-2B ITC available: ₹38,500 (10 invoices matched)
- **ITC at risk: ₹24,120** (2 invoices unmatched)

**Unmatched Invoices**
| Supplier | Taxable | ITC at Risk | Reason | ITC Source |
|---|---|---|---|---|
| Ganesh Textiles | ₹1,12,000 | ₹18,000 (IGST) | GSTR-1 not filed by supplier | tally_gst_fields |
| Modi Traders | ₹38,000 | ₹6,120* | GSTIN mismatch | ⚠ unknown — verify manually |

*ITC for Modi Traders could not be computed — GSTIN mismatch means actual GST rate unknown. **Do not assume 18%.**

**Recommended Actions**
1. Send WhatsApp to Ganesh Textiles requesting GSTR-1 upload
2. Verify Modi Traders GSTIN in Tally master data — correct to 27MODTR...

Shall I generate a supplier chase list with WhatsApp messages?`,
    sources: [
      { title: "Tally Vouchers — Mehta Exports Oct 2024", type: "tally_voucher" },
      { title: "GSTR-2B Statement — 27BBBFM8272G1ZP — Oct 2024", type: "gst_gstr2b" },
      { title: "GST Filing Status — Mehta Exports", type: "gst_filing_status" },
    ],
  },
  "summary": {
    text: `**Complete Compliance Profile — Patel Construction (GSTIN: 27AAGFP9381H1ZW)**

**Identity**
- PAN: AAGFP9381H · CIN: [Not registered — sole proprietorship]
- GST registration: Regular taxpayer, Maharashtra

**🔴 Active Notice**
- Notice u/s 142(1) — Non-filing of ITR AY 2025-26
- Issued: Oct 28 · **Response due: Nov 15 (7 days)**
- Recommended action: File ITR or seek 30-day extension

**GST Compliance (FY 2024-25)**
- GSTR-1: Filed for Apr–Sep ✓ · Oct pending (due Nov 11)
- GSTR-3B: Filed for Apr–Sep ✓ · Oct pending (due Nov 20)
- ITC reconciliation: No significant mismatches in last 3 months

**Banking** *(from HDFC bank statements)*
- Current balance: ₹12.4L (as of Oct 31)
- Average monthly turnover: ₹38L
- GST payments via NEFT: Regular and timely

**Risk Score: MEDIUM** — Notice response is the critical item this week.

Want me to draft the 142(1) reply or compute the ITR for AY 2025-26?`,
    sources: [
      { title: "GST Filing Status — Patel Construction", type: "gst_filing_status" },
      { title: "IT Portal Notice — 142(1) — AAGFP9381H", type: "it_notice" },
      { title: "Bank Statement — HDFC Oct 2024", type: "bank_summary" },
      { title: "GSTR-3B — 27AAGFP9381H1ZW — Oct 2024", type: "gst_gstr3b" },
    ],
  },
  "draft": {
    text: `**Draft — WhatsApp Message to Overdue Clients**

Here are tailored messages for each overdue client:

---
**To: Ganesh Textiles (+91XXXXXXXXXX)**
> Dear Ganesh Textiles, your GSTR-1 for October 2024 has not been uploaded. This is causing ITC blockage of ₹18,000 for our client Mehta Exports. Kindly file at the earliest. Reply DONE once uploaded. — CA Akshay Garg

---
**To: Kapoor Pharma (+91XXXXXXXXXX)**
> Dear Kapoor Pharma, ITR for FY 2024-25 is overdue by 8 days. Penalty u/s 234F of ₹5,000 has been triggered. Please share final accounts and TDS certificates at the earliest so we can file. — CA Akshay Garg

---
**To: Desai Agro (+91XXXXXXXXXX)**
> Dear Desai Agro, GSTR-3B for September 2024 is overdue. Late fee of ₹950 is accruing at ₹50/day. Kindly confirm if we should proceed with filing with available data. — CA Akshay Garg

---
Shall I trigger these via the WhatsApp Business API? The connector is active for your firm.`,
    sources: [
      { title: "Client WhatsApp Registry", type: "whatsapp_template" },
      { title: "Compliance Calendar — All Clients", type: "gst_filing_status" },
    ],
  },
};

function getMockResponse(query) {
  const q = query.toLowerCase();
  if (q.includes("overdue") || q.includes("pending") || q.includes("due")) return CA_MOCK_RESPONSES["overdue"];
  if (q.includes("itc") || q.includes("mismatch") || q.includes("reconcil")) return CA_MOCK_RESPONSES["itc mismatch"];
  if (q.includes("summary") || q.includes("profile") || q.includes("patel") || q.includes("health")) return CA_MOCK_RESPONSES["summary"];
  if (q.includes("draft") || q.includes("write") || q.includes("whatsapp") || q.includes("message")) return CA_MOCK_RESPONSES["draft"];
  return CA_MOCK_RESPONSES["overdue"]; // Default
}

// ── Source type display helpers ───────────────────────────────
const SOURCE_META = {
  tally_voucher:       { label: "Tally Voucher",        icon: "📒", color: "#6D28D9" },
  tally_ledger:        { label: "Tally Ledger",         icon: "📒", color: "#6D28D9" },
  tally_gstr9_summary: { label: "Tally GSTR-9 Export",  icon: "📒", color: "#6D28D9" },
  gst_gstr2b:          { label: "GSTR-2B",              icon: "🏛️", color: "#1D4ED8" },
  gst_gstr3b:          { label: "GSTR-3B",              icon: "🏛️", color: "#1D4ED8" },
  gst_filing_status:   { label: "GST Filing Status",    icon: "🏛️", color: "#1D4ED8" },
  it_itr_status:       { label: "ITR Status",           icon: "📋", color: "#047857" },
  it_notice:           { label: "IT Notice",            icon: "📋", color: "#B91C1C" },
  it_form_26as:        { label: "Form 26AS",            icon: "📋", color: "#047857" },
  tds_return_status:   { label: "TRACES TDS Return",   icon: "🧾", color: "#B45309" },
  bank_summary:        { label: "Bank Statement",       icon: "🏦", color: "#0F766E" },
  mca_company_master:  { label: "MCA Company",          icon: "🏢", color: "#374151" },
  whatsapp_template:   { label: "WhatsApp",             icon: "💬", color: "#15803D" },
};

function SourceChip({ source }) {
  const meta = SOURCE_META[source.type] || { label: source.type, icon: "📄", color: "#6B7280" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: meta.color + "12", color: meta.color,
      border: `1px solid ${meta.color}30`,
      borderRadius: 5, padding: "2px 8px", fontSize: 10, fontWeight: 600,
      whiteSpace: "nowrap",
    }}>
      {meta.icon} {source.title || meta.label}
    </span>
  );
}

// ── Markdown-ish renderer (no external lib) ───────────────────
function MsgContent({ text }) {
  const lines = text.split("\n");
  return (
    <div style={{ fontSize: 13, lineHeight: 1.75, color: "#1F2937" }}>
      {lines.map((line, i) => {
        // Bold **text**
        const parts = line.split(/\*\*(.*?)\*\*/g);
        const rendered = parts.map((p, j) => j % 2 === 1 ? <strong key={j}>{p}</strong> : p);

        // Table row
        if (line.startsWith("|")) {
          return <div key={i} style={{ fontFamily: "monospace", fontSize: 11, color: "#374151", whiteSpace: "pre-wrap" }}>{rendered}</div>;
        }
        // Heading
        if (line.startsWith("**") && line.endsWith("**")) {
          return <div key={i} style={{ fontWeight: 800, fontSize: 14, color: "#111827", marginTop: 12, marginBottom: 4 }}>{line.replace(/\*\*/g, "")}</div>;
        }
        // List item
        if (line.match(/^\d+\.\s|^-\s/)) {
          return <div key={i} style={{ paddingLeft: 16, display: "flex", gap: 6 }}><span style={{ flexShrink: 0, color: "#6B7280" }}>{line.match(/^\d+\./) ? line.match(/^\d+\./)[0] : "•"}</span><span>{rendered.slice(1)}</span></div>;
        }
        // HR
        if (line === "---") return <hr key={i} style={{ border: "none", borderTop: "1px solid #E5E7EB", margin: "8px 0" }} />;
        // Blank line
        if (!line.trim()) return <div key={i} style={{ height: 6 }} />;

        return <div key={i}>{rendered}</div>;
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
export function CAIntelligencePage({ bp, t, backendLive, go }) {
  const isMob = bp?.isMobile;
  const [msgs, setMsgs] = useState([
    {
      id: 0, role: "assistant",
      text: `**Welcome to CA Intelligence** 🧠

I'm your AI-powered practice assistant. I can see across all your connected sources:

📒 **Tally** (multi-company) · 🏛️ **GST Portal** · 📋 **IT Portal** · 🧾 **TRACES** · 🏢 **MCA21** · 🏦 **Bank Statements**

Ask me anything about your clients, filings, ITC gaps, notices, or compliance deadlines. I'll search across all sources and cite every claim.

*Pick a suggested question below or type your own.*`,
      sources: [],
    },
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [activeCategory, setActiveCategory] = useState("compliance");
  const [showSuggestions, setShowSuggestions] = useState(true);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const msgId = useRef(1);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, typing]);

  const send = useCallback(async (q) => {
    const query = q || input.trim();
    if (!query) return;
    setInput("");
    setShowSuggestions(false);

    const uid = msgId.current++;
    setMsgs(p => [...p, { id: uid, role: "user", text: query, sources: [] }]);
    setTyping(true);

    // Try real backend first, fall back to CA-specific demo
    let responded = false;

    if (backendLive) {
      try {
        // Use existing streaming endpoint with CA system prompt
        const res = await fetch("/api/v1/chat/conversations", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("cosfy_access") || ""}`,
          },
          body: JSON.stringify({
            title: "CA Intelligence",
            metadata: {
              mode: "ca_intelligence",
              system_prompt_variant: "ca_firm",
            },
          }),
        });
        if (res.ok) {
          const conv = await res.json();
          const streamRes = await fetch(
            `/api/v1/chat/conversations/${conv.id}/messages/stream`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("cosfy_access") || ""}`,
              },
              body: JSON.stringify({
                content: query,
                source_filter: [
                  "tally_voucher", "tally_ledger", "tally_gstr9_summary",
                  "gst_gstr3b", "gst_gstr2b", "gst_gstr1", "gst_filing_status",
                  "it_itr_status", "it_notice", "it_form_26as", "it_ais",
                  "tds_return_status", "tds_defaults", "bank_summary",
                  "mca_company_master", "mca_filing_compliance",
                ],
              }),
            }
          );
          if (streamRes.ok) {
            // Stream handling — collect chunks
            const reader = streamRes.body.getReader();
            const decoder = new TextDecoder();
            let fullText = "";
            const aid = msgId.current++;
            setMsgs(p => [...p, { id: aid, role: "assistant", text: "", sources: [], streaming: true }]);
            setTyping(false);

            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              const chunk = decoder.decode(value);
              const lines = chunk.split("\n");
              for (const line of lines) {
                if (line.startsWith("data: ")) {
                  try {
                    const data = JSON.parse(line.slice(6));
                    if (data.type === "token") {
                      fullText += data.content;
                      setMsgs(p => p.map(m => m.id === aid ? { ...m, text: fullText } : m));
                    }
                    if (data.type === "sources") {
                      setMsgs(p => p.map(m => m.id === aid ? { ...m, sources: data.sources || [], streaming: false } : m));
                    }
                  } catch { /* skip malformed */ }
                }
              }
            }
            responded = true;
          }
        }
      } catch { /* fall through to demo */ }
    }

    if (!responded) {
      // CA demo mode — realistic mock responses
      await new Promise(r => setTimeout(r, 1400 + Math.random() * 800));
      setTyping(false);
      const mock = getMockResponse(query);
      const aid = msgId.current++;
      setMsgs(p => [...p, { id: aid, role: "assistant", text: mock.text, sources: mock.sources }]);
    }
  }, [input, backendLive]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const activeCat = PROMPT_CATEGORIES.find(c => c.id === activeCategory);

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden", background: t?.bg || "#F4F6FA" }}>

      {/* ── LEFT: Suggestions panel (desktop only) ── */}
      {!isMob && (
        <div style={{
          width: 260, flexShrink: 0, borderRight: `1px solid ${t?.bd || "#E3E8F0"}`,
          background: t?.sf || "#fff", display: "flex", flexDirection: "column",
          overflow: "hidden",
        }}>
          <div style={{ padding: "16px 16px 10px", borderBottom: `1px solid ${t?.bd || "#E3E8F0"}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: "linear-gradient(135deg,#6366F1,#06B6D4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>🧠</div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 13, color: t?.tx || "#141C2E" }}>CA Intelligence</div>
                <div style={{ fontSize: 9, color: t?.txM || "#718096", letterSpacing: .5, textTransform: "uppercase" }}>Personalized Practice AI</div>
              </div>
            </div>
          </div>

          {/* Category tabs */}
          <div style={{ display: "flex", flexDirection: "column", gap: 2, padding: "10px 8px 6px", borderBottom: `1px solid ${t?.bd || "#E3E8F0"}` }}>
            {PROMPT_CATEGORIES.map(cat => (
              <button key={cat.id} onClick={() => setActiveCategory(cat.id)} style={{
                display: "flex", alignItems: "center", gap: 8, width: "100%",
                padding: "7px 10px", borderRadius: 6, border: "none", cursor: "pointer",
                background: activeCategory === cat.id ? cat.bg : "transparent",
                color: activeCategory === cat.id ? cat.color : (t?.txM || "#718096"),
                fontSize: 12, fontWeight: activeCategory === cat.id ? 700 : 500, textAlign: "left",
              }}>
                {cat.label}
              </button>
            ))}
          </div>

          {/* Suggested prompts for active category */}
          <div style={{ flex: 1, overflowY: "auto", padding: "8px" }}>
            {activeCat?.prompts.map((prompt, i) => (
              <button key={i} onClick={() => send(prompt)} style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "8px 10px", margin: "3px 0", borderRadius: 6,
                border: `1px solid ${activeCat.color}20`,
                background: activeCat.bg, color: "#374151",
                fontSize: 11, cursor: "pointer", lineHeight: 1.5,
                transition: "all .15s",
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = activeCat.color + "60"; e.currentTarget.style.background = activeCat.color + "10"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = activeCat.color + "20"; e.currentTarget.style.background = activeCat.bg; }}>
                {prompt}
              </button>
            ))}
          </div>

          {/* Connected sources */}
          <div style={{ padding: "12px", borderTop: `1px solid ${t?.bd || "#E3E8F0"}`, background: t?.sf2 || "#F8FAFD" }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: t?.txM || "#718096", letterSpacing: .6, textTransform: "uppercase", marginBottom: 6 }}>Searching across</div>
            {[["📒","Tally (multi-co)"],["🏛️","GST Portal"],["📋","IT Portal + AIS"],["🧾","TRACES TDS"],["🏢","MCA21"],["🏦","Bank Statements"]].map(([ic, label]) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#10B981", flexShrink: 0 }} />
                <span style={{ fontSize: 10, color: t?.txM || "#718096" }}>{ic} {label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── RIGHT: Chat area ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>

        {/* Header */}
        <div style={{ padding: "12px 20px", background: t?.sf || "#fff", borderBottom: `1px solid ${t?.bd || "#E3E8F0"}`, display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: t?.tx || "#141C2E" }}>🧠 CA Intelligence</div>
            <div style={{ fontSize: 11, color: t?.txM || "#718096" }}>
              Ask anything · Cites Tally, GST, IT Portal, TRACES, MCA21, Banks
            </div>
          </div>
          {!backendLive && (
            <span style={{ fontSize: 10, background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A", padding: "2px 8px", borderRadius: 4, fontWeight: 600 }}>
              Demo mode
            </span>
          )}
          <button onClick={() => { setMsgs(msgs.slice(0, 1)); setShowSuggestions(true); }}
            style={{ background: "none", border: `1px solid ${t?.bd || "#E3E8F0"}`, borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", color: t?.txM || "#718096", fontWeight: 600 }}>
            New chat
          </button>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: "auto", padding: isMob ? "16px 12px" : "20px 24px" }}>
          <div style={{ maxWidth: 760, margin: "0 auto" }}>
            {msgs.map(m => (
              <div key={m.id} style={{ marginBottom: 24, display: "flex", flexDirection: "column", alignItems: m.role === "user" ? "flex-end" : "flex-start" }}>
                {m.role === "assistant" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <div style={{ width: 26, height: 26, borderRadius: 7, background: "linear-gradient(135deg,#6366F1,#06B6D4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>🧠</div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: t?.tx2 || "#4A5568" }}>Jaanch AI</span>
                    <span style={{ fontSize: 10, color: t?.txD || "#A0AEC0" }}>· All sources</span>
                  </div>
                )}
                <div style={{
                  maxWidth: "92%", padding: "14px 18px", borderRadius: 12,
                  background: m.role === "user" ? "linear-gradient(135deg,#1847C8,#0EA5E9)" : (t?.sf || "#fff"),
                  color: m.role === "user" ? "#fff" : (t?.tx || "#141C2E"),
                  border: m.role === "assistant" ? `1px solid ${t?.bd || "#E3E8F0"}` : "none",
                  borderBottomRightRadius: m.role === "user" ? 3 : 12,
                  borderBottomLeftRadius: m.role === "assistant" ? 3 : 12,
                  boxShadow: m.role === "assistant" ? "0 1px 4px rgba(0,0,0,.06)" : "none",
                }}>
                  {m.role === "user"
                    ? <div style={{ fontSize: 13, lineHeight: 1.6 }}>{m.text}</div>
                    : <MsgContent text={m.text} />
                  }
                  {m.streaming && (
                    <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
                      {[0,1,2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: "#6366F1", opacity: 0.6, animation: `bounce 1s ${i * 0.2}s infinite` }} />)}
                    </div>
                  )}
                </div>

                {/* Source citations */}
                {m.role === "assistant" && m.sources?.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8, maxWidth: "92%" }}>
                    <span style={{ fontSize: 10, color: t?.txD || "#A0AEC0", alignSelf: "center" }}>Sources:</span>
                    {m.sources.map((s, i) => <SourceChip key={i} source={s} />)}
                  </div>
                )}

                {/* Quick action buttons for CA responses */}
                {m.role === "assistant" && m.id > 0 && (
                  <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                    {[
                      ["📋 Copy", () => navigator.clipboard?.writeText(m.text).catch(() => {})],
                      ["🔄 GST Recon", () => go("gst_recon")],
                      ["📅 Calendar", () => go("compliance")],
                    ].map(([label, action]) => (
                      <button key={label} onClick={action} style={{
                        background: "none", border: `1px solid ${t?.bd || "#E3E8F0"}`,
                        borderRadius: 5, padding: "3px 10px", fontSize: 10, cursor: "pointer",
                        color: t?.txM || "#718096", fontWeight: 600,
                      }}>
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {typing && (
              <div style={{ marginBottom: 24, display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div style={{ width: 26, height: 26, borderRadius: 7, background: "linear-gradient(135deg,#6366F1,#06B6D4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>🧠</div>
                <div style={{ background: t?.sf || "#fff", border: `1px solid ${t?.bd || "#E3E8F0"}`, borderRadius: "12px 12px 12px 3px", padding: "14px 18px", display: "flex", gap: 5, alignItems: "center" }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: "#6366F1", opacity: 0.5 }} />
                  ))}
                  <span style={{ fontSize: 11, color: t?.txM || "#718096", marginLeft: 4 }}>Searching across all sources…</span>
                </div>
              </div>
            )}

            {/* Mobile suggested prompts */}
            {isMob && showSuggestions && msgs.length === 1 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: t?.txM || "#718096", textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>Try asking</div>
                {PROMPT_CATEGORIES.slice(0, 2).flatMap(c => c.prompts.slice(0, 2)).map((p, i) => (
                  <button key={i} onClick={() => send(p)} style={{
                    display: "block", width: "100%", textAlign: "left", marginBottom: 6,
                    padding: "10px 12px", borderRadius: 8, border: `1px solid ${t?.bd || "#E3E8F0"}`,
                    background: t?.sf || "#fff", color: t?.tx2 || "#4A5568",
                    fontSize: 12, cursor: "pointer", lineHeight: 1.5,
                  }}>
                    {p}
                  </button>
                ))}
              </div>
            )}

            <div ref={endRef} />
          </div>
        </div>

        {/* Input */}
        <div style={{ padding: isMob ? "12px" : "16px 24px", borderTop: `1px solid ${t?.bd || "#E3E8F0"}`, background: t?.sf || "#fff", flexShrink: 0 }}>
          <div style={{ maxWidth: 760, margin: "0 auto" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end", background: t?.sf2 || "#F8FAFD", border: `1px solid ${t?.bd || "#E3E8F0"}`, borderRadius: 12, padding: "8px 12px", transition: "border-color .15s" }}
              onFocusCapture={e => e.currentTarget.style.borderColor = "#6366F1"}
              onBlurCapture={e => e.currentTarget.style.borderColor = t?.bd || "#E3E8F0"}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Ask about any client, filing, ITC gap, notice, deadline…"
                rows={1}
                style={{
                  flex: 1, background: "none", border: "none", resize: "none",
                  color: t?.tx || "#141C2E", fontSize: 13, lineHeight: 1.6,
                  outline: "none", fontFamily: "inherit", maxHeight: 120, overflowY: "auto",
                }}
                onInput={e => {
                  e.target.style.height = "auto";
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
                }}
              />
              <button
                onClick={() => send()}
                disabled={!input.trim() || typing}
                style={{
                  width: 36, height: 36, borderRadius: 8, border: "none", cursor: input.trim() && !typing ? "pointer" : "default",
                  background: input.trim() && !typing ? "linear-gradient(135deg,#6366F1,#06B6D4)" : "#E5E7EB",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 16, flexShrink: 0, transition: "all .15s",
                }}>
                {typing ? "⏳" : "↑"}
              </button>
            </div>
            <div style={{ fontSize: 10, color: t?.txD || "#A0AEC0", marginTop: 6, textAlign: "center" }}>
              Searches Tally · GST Portal · IT Portal · TRACES · MCA21 · Banks · WhatsApp in real-time
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
}
